import { useState } from "react";

import { SetupTab } from "./tabs/SetupTab";
import { ConfigurationTab } from "./tabs/ConfigurationTab";
import { LegendTab } from "./tabs/LegendTab";
// icon
import CloseIcon from "@mui/icons-material/Close";
import AssessmentIcon from '@mui/icons-material/Assessment';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import GavelIcon from '@mui/icons-material/Gavel';

export function PerformanceAppraisalSetupForm({
  isOpen,
  modalName,
  onClose,
  form,
  setForm,
  handleSubmit,
  mode = "add",
  showGeneralMessage,
  user,
  searchEmployees,
  performanceCategories,
  performanceObjectives,
  performanceRatingsLegends,
  selectedData
}) {
  const [activeTab, setActiveTab] = useState("setup");
  const [errorState, setErrorState] = useState({
    employee: false,
    rater: false,
    dateStart: false,
    dateEnd: false,
    status: false
  });

  // Employee search states
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [employeeResults, setEmployeeResults] = useState([]);
  const [showEmployeeSearch, setShowEmployeeSearch] = useState(false);

  // Rater search states
  const [raterSearchTerm, setRaterSearchTerm] = useState("");
  const [raterResults, setRaterResults] = useState([]);
  const [showRaterSearch, setShowRaterSearch] = useState(false);

  // Configuration states
  const [newCategory, setNewCategory] = useState({ description: "", totalPercentage: "" });
  const [editingCategory, setEditingCategory] = useState(null);
  const [newObjective, setNewObjective] = useState({ 
    performanceCategoriesId: "", 
    code: "", 
    description: "", 
    percentage: "" 
  });

  const submit = (e) => {
    e.preventDefault();

    // Validation based on active tab
    if (activeTab === "setup") {
      const newErrorState = {
        employee: !form.employeePersonalInformationId,
        rater: !form.raterEmployeePersonalInformationId,
        dateStart: !form.dateStart,
        dateEnd: !form.dateEnd,
        status: !form.status
      };

      if (form.dateStart && form.dateEnd) {
        const startDate = new Date(form.dateStart);
        const endDate = new Date(form.dateEnd);
        if (endDate < startDate) {
          showGeneralMessage("End date cannot be earlier than start date", false);
          return;
        }
      }

      if (newErrorState.employee || newErrorState.rater || newErrorState.dateStart || newErrorState.dateEnd || newErrorState.status) {
        setErrorState(newErrorState);
        return;
      }
    }

    handleSubmit(e);
  };

  const handleClose = () => {
    onClose();
    setErrorState({ 
      employee: false, 
      rater: false, 
      dateStart: false, 
      dateEnd: false,
      status: false  // Add this
    });
    setActiveTab("setup");
  };

  const handleInputChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Employee search functions
  const handleEmployeeSearchChange = async (value) => {
    setEmployeeSearchTerm(value);
    if (value.length >= 2) {
      const results = await searchEmployees(value);
      setEmployeeResults(results);
    } else {
      setEmployeeResults([]);
    }
  };

  const handleRaterSearchChange = async (value) => {
    setRaterSearchTerm(value);
    if (value.length >= 2) {
      const results = await searchEmployees(value);
      setRaterResults(results);
    } else {
      setRaterResults([]);
    }
  };

  const selectEmployee = (employee) => {
    setForm(prev => ({
      ...prev,
      employeePersonalInformationId: employee.id,
      employeeData: {
        id: employee.id,
        fullName: `${employee.firstName} ${employee.lastName}`.trim(),
        employeeId: employee.employeeId,
        dateHired: employee.dateHired,
        department: employee.department,
        position: employee.position
      }
    }));
    setEmployeeSearchTerm("");
    setEmployeeResults([]);
    setShowEmployeeSearch(false);
  };

  const selectRater = (employee) => {
    setForm(prev => ({
      ...prev,
      raterEmployeePersonalInformationId: employee.id,
      raterData: {
        id: employee.id,
        fullName: `${employee.firstName} ${employee.lastName}`.trim(),
        position: employee.position
      }
    }));
    setRaterSearchTerm("");
    setRaterResults([]);
    setShowRaterSearch(false);
  };

  // Category management functions
  const addCategory = () => {
    if (!newCategory.description || !newCategory.totalPercentage) {
      showGeneralMessage("Please fill all category fields", false);
      return;
    }

    const category = {
      id: Date.now(), // Temporary ID
      description: newCategory.description,
      totalPercentage: parseFloat(newCategory.totalPercentage),
      objectives: []
    };

    setForm(prev => ({
      ...prev,
      performanceCategories: [...prev.performanceCategories, category]
    }));

    setNewCategory({ description: "", totalPercentage: "" });
  };

  const updateCategory = () => {
    if (!editingCategory) return;

    setForm(prev => ({
      ...prev,
      performanceCategories: prev.performanceCategories.map(cat =>
        cat.id === editingCategory.id ? editingCategory : cat
      )
    }));

    setEditingCategory(null);
  };

  const deleteCategory = (categoryId) => {
    setForm(prev => ({
      ...prev,
      performanceCategories: prev.performanceCategories.filter(cat => cat.id !== categoryId)
    }));
  };

  // Objective management functions
  const addObjective = () => {
    if (!newObjective.performanceCategoriesId || !newObjective.description || !newObjective.percentage) {
      showGeneralMessage("Please fill all objective fields", false);
      return;
    }

    const objective = {
      id: Date.now(), // Temporary ID
      performanceCategoriesId: parseInt(newObjective.performanceCategoriesId),
      code: newObjective.code || `KPI ${Date.now().toString().slice(-4)}`,
      description: newObjective.description,
      percentage: parseFloat(newObjective.percentage)
    };

    setForm(prev => ({
      ...prev,
      performanceCategories: prev.performanceCategories.map(cat =>
        cat.id === parseInt(newObjective.performanceCategoriesId)
          ? { ...cat, objectives: [...(cat.objectives || []), objective] }
          : cat
      )
    }));

    setNewObjective({ 
      performanceCategoriesId: "", 
      code: "", 
      description: "", 
      percentage: "" 
    });
  };

  if (!isOpen) return null;

  const isEditMode = mode === "edit";

  return (
    <>
      <div
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 z-[10000] flex items-center justify-center overflow-auto bg-black/50 p-3 text-gray-700"
      >
        <div className="relative flex h-auto max-h-[90vh] w-full flex-col rounded-lg bg-gray-50 shadow-xl md:w-[90%] lg:w-[90%] xl:w-[80%]">

          {/* Header */}
          <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-lg border-b border-gray-200 bg-white px-6 py-2 shadow-sm">
            <h2 className="flex items-center space-x-2 text-xl font-semibold text-gray-800">
              <AssessmentIcon className="text-blue-500" />
              <span className="text-xl text-gray-600">{modalName}</span>
            </h2>
            <button
              onClick={handleClose}
              className="border-none bg-transparent p-1 text-gray-400 transition-colors hover:text-gray-700"
              aria-label="Close modal"
            >
              <CloseIcon fontSize="medium" />
            </button>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200 bg-white pt-3">
            <div className="flex space-x-1 px-6">
              <button
                onClick={() => setActiveTab("setup")}
                className={`px-4 py-3 text-sm font-medium rounded-t-lg transition-colors border-b-2 ${activeTab === "setup"
                  ? "border-blue-500 text-blue-600 bg-blue-50"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
              >
                <PeopleIcon fontSize="small" className="mr-2" />
                Setup
              </button>
              <button
                onClick={() => setActiveTab("configuration")}
                className={`px-4 py-3 text-sm font-medium rounded-t-lg transition-colors border-b-2 ${activeTab === "configuration"
                  ? "border-blue-500 text-blue-600 bg-blue-50"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
              >
                <SettingsIcon fontSize="small" className="mr-2" />
                Configuration
              </button>
              <button
                onClick={() => setActiveTab("legend")}
                className={`px-4 py-3 text-sm font-medium rounded-t-lg transition-colors border-b-2 ${activeTab === "legend"
                  ? "border-blue-500 text-blue-600 bg-blue-50"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                  }`}
              >
                <GavelIcon fontSize="small" className="mr-2" />
                Legend
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === "setup" && (
              <SetupTab
                form={form}
                errorState={errorState}
                handleInputChange={handleInputChange}
                employeeSearchTerm={employeeSearchTerm}
                handleEmployeeSearchChange={handleEmployeeSearchChange}
                employeeResults={employeeResults}
                selectEmployee={selectEmployee}
                showEmployeeSearch={showEmployeeSearch}
                setShowEmployeeSearch={setShowEmployeeSearch}
                raterSearchTerm={raterSearchTerm}
                handleRaterSearchChange={handleRaterSearchChange}
                raterResults={raterResults}
                selectRater={selectRater}
                showRaterSearch={showRaterSearch}
                setShowRaterSearch={setShowRaterSearch}
              />
            )}

            {activeTab === "configuration" && (
              <ConfigurationTab
                form={form}
                setForm={setForm}
                newCategory={newCategory}
                setNewCategory={setNewCategory}
                editingCategory={editingCategory}
                setEditingCategory={setEditingCategory}
                newObjective={newObjective}
                setNewObjective={setNewObjective}
                addCategory={addCategory}
                updateCategory={updateCategory}
                deleteCategory={deleteCategory}
                addObjective={addObjective}
              />
            )}

            {activeTab === "legend" && (
              <LegendTab
                performanceRatingsLegends={performanceRatingsLegends}
                form={form}
                handleInputChange={handleInputChange}
              />
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-between space-x-2 rounded-b-lg border-t border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex items-center text-sm text-gray-600">
              {activeTab === "configuration" && (
                <span>
                  {form.performanceCategories?.length || 0} categories configured
                </span>
              )}
              {activeTab === "appraisal" && (
                <span>
                  {form.scores?.length || 0} scores recorded
                </span>
              )}
            </div>

            <div className="flex space-x-2">
              <button
                onClick={handleClose}
                className="cursor-pointer rounded border-none bg-gray-400 px-4 py-2 text-white transition-colors hover:bg-gray-500"
              >
                Cancel
              </button>
              {activeTab !== "appraisal" && (
                <button
                  onClick={submit}
                  className="cursor-pointer rounded border-none bg-blue-500 px-4 py-2 text-white transition-colors hover:bg-blue-600"
                >
                  {isEditMode ? "Update" : "Save"}
                </button>
              )}
            </div>
          </div>

        </div>
      </div>
    </>
  );
}